import Avatar from "react-avatar";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { backendService } from "@/lib/backend";
import { CHOSEN_CORE_VALUES_LIMIT } from "@/lib/constants";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { getFullName } from "@/lib/getFullName";
import { queryOptions_coreValuesChosen } from "@/lib/query/functions/core-values/chosen";
import { useMe } from "@/lib/query/functions/user/me";
import { useMyProgression } from "@/lib/query/functions/user/progression";
import { useSelectorModalStore } from "@/lib/store/modal";
import { cx } from "@/lib/style/cva.config";
import { CoreValueBubble } from "../CoreValue/Bubble";

const ICON_SIZE = 128;
const STROKE_WIDTH = 6;

export function DynamicAvatar({
	accuracyType = "full",
	showCoreValueBubbles = true,
	className,
}: {
	accuracyType?: "small" | "full" | "none";
	showCoreValueBubbles?: boolean;
	className?: string;
}) {
	const { t } = useTranslation();
	const [me] = useMe();
	const [progress] = useMyProgression();
	const present = useSelectorModalStore.use.present();

	const selectedCoreValuesQuery = useQuery({
		queryKey: ["coreValue", "selected"],
		queryFn: () => backendService.corevalues.selectedCoreValuesControllerGetAll(),
	});

	const { data: coreValues } = useQuery(queryOptions_coreValuesChosen());
	const showAccuracy = (coreValues?.length ?? 0) < CHOSEN_CORE_VALUES_LIMIT;

	const selectedCoreValues =
		!selectedCoreValuesQuery?.data?.data?.length ||
		selectedCoreValuesQuery?.data?.data?.length < CHOSEN_CORE_VALUES_LIMIT
			? []
			: selectedCoreValuesQuery?.data?.data
					.toSorted(() => Math.random() - 0.5)
					.slice(0, CHOSEN_CORE_VALUES_LIMIT)
					.map((coreValue) => coreValue.coreValue);

	const shouldShowBubbles =
		showCoreValueBubbles &&
		!selectedCoreValuesQuery.isLoading &&
		(selectedCoreValuesQuery?.data?.data?.length ?? 0) >= CHOSEN_CORE_VALUES_LIMIT &&
		selectedCoreValues.length === CHOSEN_CORE_VALUES_LIMIT;
	const circumference = (2 * Math.PI * (ICON_SIZE + STROKE_WIDTH * 2)) / 2;
	const avatarUrl = getAvatarUrl(me);
	const fullName = me ? getFullName(me) : "?";

	return (
		<div className={cx("relative flex w-full items-center justify-center", className)}>
			<div className="bg-brown-300 relative rounded-full" style={{ padding: STROKE_WIDTH }}>
				{avatarUrl ? (
					<div
						style={{
							backgroundImage: `url(${avatarUrl})`,
							width: ICON_SIZE,
							height: ICON_SIZE,
						}}
						className="size-full rounded-full bg-cover bg-center"
					/>
				) : (
					<div
						style={{
							width: ICON_SIZE,
							height: ICON_SIZE,
						}}
						className="size-full rounded-full"
					>
						<Avatar name={fullName} size={ICON_SIZE.toString()} round className="size-full" />
					</div>
				)}

				{accuracyType !== "none" && (
					<div className="absolute top-0 left-0 size-full">
						<svg className="size-full -rotate-90 overflow-hidden rounded-full">
							<circle
								cx="50%"
								cy="50%"
								r="50%"
								className="stroke-inset fill-transparent stroke-orange-500"
								strokeWidth={STROKE_WIDTH * 2}
								strokeDasharray={`${circumference} ${circumference}`}
								strokeDashoffset={`${circumference - (showAccuracy ? progress.core_value_accuracy / 100 : 1) * circumference}`}
							/>
						</svg>
					</div>
				)}

				{showAccuracy && accuracyType === "small" && (
					<button
						type="button"
						className="absolute right-0 bottom-1.5 m-0 cursor-pointer appearance-none border-none bg-transparent p-0 focus:outline-none"
						onClick={() => present("coreValuesAccuracyDetailsExplainer")}
						aria-label={t("core-values.accuracy.small", {
							accuracy: progress.core_value_accuracy,
						})}
					>
						<span className="bg-brown-200 flex items-center justify-center rounded-full px-2.5 py-1 text-sm font-bold text-[#78675E]">
							{t("core-values.accuracy.small", {
								accuracy: progress.core_value_accuracy,
							})}
						</span>
					</button>
				)}

				{showAccuracy && accuracyType === "full" && (
					<button
						type="button"
						className="absolute -bottom-2 left-1/2 m-0 -translate-x-1/2 cursor-pointer appearance-none border-none bg-transparent p-0 focus:outline-none"
						onClick={() => present("coreValuesAccuracyDetailsExplainer")}
						aria-label={t("core-values.accuracy.full", {
							accuracy: progress.core_value_accuracy,
						})}
					>
						<span className="bg-brown-100 flex items-center justify-center rounded-full px-2.5 py-1 text-sm font-bold text-nowrap text-[#78675E]">
							{t("core-values.accuracy.full", {
								accuracy: progress.core_value_accuracy,
							})}
						</span>
					</button>
				)}

				{shouldShowBubbles && selectedCoreValues.length > 0 && (
					<>
						<CoreValueBubble
							value={selectedCoreValues[0]}
							transform={{ top: -16, left: -112, scale: 4 }}
						/>
						<CoreValueBubble
							value={selectedCoreValues[1]}
							transform={{ bottom: 0, right: -108, scale: 3.75 }}
						/>
						<CoreValueBubble
							value={selectedCoreValues[2]}
							transform={{ bottom: -20, left: -100, scale: 3.5 }}
						/>
						<CoreValueBubble
							value={selectedCoreValues[3]}
							transform={{ top: 4, right: -64, scale: 3.25 }}
						/>
						<CoreValueBubble
							value={selectedCoreValues[4]}
							transform={{ top: 48, left: -60, scale: 3 }}
						/>
					</>
				)}
			</div>
		</div>
	);
}
